import React, { useState, useEffect, useContext } from "react";

import { useQuery, useMutation, useApolloClient } from "react-apollo";
import { useParams, useLocation, Route, useHistory } from "react-router-dom";
import gql from "graphql-tag";
import { Title, Field, Control, Button, Icon, Tab } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ModalContext } from "../../../components/ModalProvider";
import { Confirmation } from "../../../components/modals";
import Loading from "../../../components/Loading";

import {
  SINGLE_ROUTE_QUERY,
  UPDATE_ROUTE_MUTATION,
  DELETE_ROUTE_MUTATION,
  ALL_ROUTES_QUERY
} from "../../../graphql/Routes/Routes.graphql";

import { ALL_ORDERS_VIEW_QUERY } from "../../../graphql/Orders/Orders.graphql";
import { ALL_DIRECTIONS_QUERY } from "../../../graphql/Directions/Directions.graphql";

import IconLink from "../../../components/IconLink";

import Details from "./routes/Details";
import Billing from "./routes/Billing";

import { PrintDirections, AddRouteForm } from "../../../components/Routes";

import Authorization, {
  useAuthorization
} from "../../../components/Authorization";
import { getUTCFormattedDate, ourToast as toast } from "../../../utils";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const INITIAL_STATE = {
  CustomerRecIdRef: "",
  RouteId: "",
  RouteDescription: "",
  Contract: "",
  PaymentMethodRecIdRef: "",
  StartLocationRecIdRef: "",
  PickupLocationRecIdRef: "",
  DeliveryLocationRecIdRef: "",
  EndingLocationRecIdRef: "",
  LoadTypeRecIdRef: "",
  IndustrialFlag: "",
  MotorOilFlag: "",
  UnloadedMiles: "",
  LoadedMiles: "",
  ChargeForTolls: "",
  Tolls: false,
  NonBill: false,
  Times: "",
  LoadTime: "",
  TravelTime: "",
  UnloadTime: "",
  ReturnToTerminalTime: "",
  Status: "",
  StartDate: "",
  EndDate: "",
  BillToRecIdRef: "",
  BookingCodeRecIdRef: "",
  TariffRecIdRef: "",
  MaxLoadTime: "",
  MaxUnloadTime: "",
  PaymentTermsRecIdRef: ""
};

const BillingAuth = Authorization(["ADMIN", "FIN"]);

const RoutePage = () => {
  const [codes, setCodes] = useState(null);
  const SystemCodesData = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["BookingCodes", "Route"],
        CodeName_in: ["BookCatg", "LoadType"],
        CodeId_in: ["O", "OE"]
      }
    }
  });
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const isAuthorized = useAuthorization(["ADMIN", "FIN"]);
  const isAdmin = useAuthorization(["ADMIN"]);
  const client = useApolloClient();
  const [isLoading, setLoading] = useState(false);

  const { setModalOpen } = useContext(ModalContext);

  const { data, loading } = useQuery(SINGLE_ROUTE_QUERY, {
    variables: { where: { RecId: parseInt(id) || null } }
  });

  const [updateRoute, resUpdate] = useMutation(UPDATE_ROUTE_MUTATION, {
    refetchQueries: ["SINGLE_ROUTE_QUERY", "ALL_ROUTES_QUERY"]
  });
  const [editing, setEditing] = useState(false);
  const [inputs, setInputs] = useState(INITIAL_STATE);
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    if (data && data?.Route && !data.loading) {
      setInputs(data?.Route);
    }
  }, [data]);

  const toggleEditing = () => setEditing(!editing);

  useEffect(() => {
    if (SystemCodesData?.data?.SystemCodes && !codes) {
      const { SystemCodes } = SystemCodesData.data;
      setCodes({
        OrdersBookingCode: SystemCodes.find(
          s => s.CodeId === "O" && s.Category === "BookingCodes"
        ),
        OneWayEmptyType: SystemCodes.find(
          s => s.CodeId === "OE" && s.Category === "Route"
        )
      });
    }
  }, [SystemCodesData, codes]);

  const handleChange = async e => {
    setEdited(true);
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updates = { ...inputs, [e.target.name]: value };
    // if changing load type to one way empty
    // bind pickup to start and end to delivery
    if (
      e.target.name === "LoadTypeRecIdRef" &&
      parseInt(value) === codes.OneWayEmptyType.RecId
    ) {
      updates.PickupLocationRecIdRef = updates.StartLocationRecIdRef;
      updates.EndingLocationRecIdRef = updates.DeliveryLocationRecIdRef;
    }
    // if load type is equal to one way empty bind start / pickup
    // and delivery / ending
    if (parseInt(updates.LoadTypeRecIdRef) === codes.OneWayEmptyType.RecId) {
      if (
        ["StartLocationRecIdRef", "PickupLocationRecIdRef"].includes(
          e.target.name
        )
      ) {
        updates.StartLocationRecIdRef = value;
        updates.PickupLocationRecIdRef = value;
      }
      if (
        ["DeliveryLocationRecIdRef", "EndingLocationRecIdRef"].includes(
          e.target.name
        )
      ) {
        updates.DeliveryLocationRecIdRef = value;
        updates.EndingLocationRecIdRef = value;
      }
    }

    // If Pickup Location or Delivery location changed
    if (
      (["PickupLocationRecIdRef", "DeliveryLocationRecIdRef"].includes(
        e.target.name
      ) ||
        (["StartLocationRecIdRef", "EndingLocationRecIdRef"].includes(
          e.target.name
        ) &&
          parseInt(updates.LoadTypeRecIdRef) ===
            codes?.OneWayEmptyType?.RecId)) &&
      updates.DeliveryLocationRecIdRef &&
      updates.PickupLocationRecIdRef
    ) {
      const { data: DirectionData } = await client.query({
        query: ALL_DIRECTIONS_QUERY,
        variables: {
          where: {
            OriginLocationRecIdRef: parseInt(updates.PickupLocationRecIdRef),
            DestinationLocationRecIdRef: parseInt(
              updates.DeliveryLocationRecIdRef
            )
          },
          orderByMulti: ["Priority_ASC"],
          skip: 0,
          first: 1
        }
      });
      if (DirectionData?.Directions?.length > 0) {
        const {
          EmptyMiles,
          LoadedMiles,
          LoadingTime,
          EmptyTravelTime,
          LoadedTravelTime,
          UnloadingTime
        } = DirectionData.Directions[0];
        updates.UnloadedMiles = EmptyMiles;
        updates.LoadedMiles = LoadedMiles;
        updates.LoadTime = LoadingTime;
        updates.TravelTime = EmptyTravelTime + LoadedTravelTime;
        updates.ReturnToTerminalTime = EmptyTravelTime;
        updates.UnloadTime = UnloadingTime;
      } else {
        toast.error(
          "No directions exists between the selected locations. Please add some."
        );
        updates.UnloadedMiles = "";
        updates.LoadedMiles = "";
        updates.LoadTime = "";
        updates.TravelTime = "";
        updates.ReturnToTerminalTime = "";
        updates.UnloadTime = "";
      }
    } else if (e.target.name === "ChargeForTolls") {
      if (value === 0 || value === null || value === "") {
        updates.Tolls = false;
      } else {
        updates.Tolls = true;
      }
    } else if (e.target.name === "Tolls" && (!value || value === "")) {
      updates.ChargeForTolls = 0.0;
    }
    setInputs(updates);
  };

  const handleDirectionsPrint = () => {
    setModalOpen(
      true,
      <PrintDirections
        delivery={inputs.DeliveryLocationRecIdRef}
        ending={inputs.EndingLocationRecIdRef}
        pickup={inputs.PickupLocationRecIdRef}
        start={inputs.StartLocationRecIdRef}
        onComplete={() => setModalOpen(false, "")}
      />
    );
  };

  const handleSave = async (e, goTo, printDirections) => {
    if (e) {
      e.preventDefault();
    }
    try {
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );
      const {
        data: { Routes }
      } = await client.query({
        query: ALL_ROUTES_QUERY,
        variables: {
          where: {
            RouteId: inputs.RouteId,
            CustomerRecIdRef: parseInt(inputs.CustomerRecIdRef),
            RecId_not: parseInt(id)
          },
          first: 1
        }
      });

      if (Routes?.length === 0) {
        await updateRoute({
          variables: {
            data: {
              ...obj,
              ChargeForTolls: parseFloat(inputs.ChargeForTolls),
              CustomerRecIdRef: parseInt(inputs.CustomerRecIdRef),
              LoadTypeRecIdRef: parseInt(inputs.LoadTypeRecIdRef),
              PaymentMethodRecIdRef: parseInt(inputs.PaymendMethodRecIdRef),
              StartLocationRecIdRef: parseInt(inputs.StartLocationRecIdRef),
              PickupLocationRecIdRef: parseInt(inputs.PickupLocationRecIdRef),
              DeliveryLocationRecIdRef: parseInt(
                inputs.DeliveryLocationRecIdRef
              ),
              EndingLocationRecIdRef: parseInt(inputs.EndingLocationRecIdRef),
              BillToRecIdRef: parseInt(inputs.BillToRecIdRef),
              BookingCodeRecIdRef: inputs.BookingCodeRecIdRef
                ? parseInt(inputs.BookingCodeRecIdRef)
                : null,
              StartDate: inputs.StartDate
                ? getUTCFormattedDate(inputs.StartDate)
                : null,
              EndDate: inputs.EndDate
                ? getUTCFormattedDate(inputs.EndDate)
                : null,
              LoadTime: inputs.LoadTime ? parseFloat(inputs.LoadTime) : null,
              UnloadTime: inputs.UnloadTime
                ? parseFloat(inputs.UnloadTime)
                : null,
              LoadedMiles: inputs.LoadedMiles
                ? parseInt(inputs.LoadedMiles)
                : null,
              ReturnToTerminalTime: inputs.ReturnToTerminalTime
                ? parseFloat(inputs.ReturnToTerminalTime)
                : null,
              TariffRecIdRef: obj.TariffRecIdRef
                ? parseInt(obj.TariffRecIdRef)
                : null,
              MaxLoadTime: obj.MaxLoadTime
                ? parseFloat(obj.MaxLoadTime, 10)
                : null,
              MaxUnloadTime: obj.MaxUnloadTime
                ? parseFloat(obj.MaxUnloadTime, 10)
                : null,
              PaymentTermsRecIdRef: obj.PaymentTermsRecIdRef
                ? parseInt(obj.PaymentTermsRecIdRef)
                : null,
              UnloadedMiles: obj.UnloadedMiles
                ? parseFloat(obj.UnloadedMiles, 10)
                : null,
              TravelTime: obj.TravelTime ? parseFloat(obj.TravelTime, 10) : null
            },
            where: { RecId: parseInt(id) }
          }
        });
        if (goTo) {
          history.push(goTo);
        } else if (printDirections) {
          handleDirectionsPrint();
        } else {
          toast.success("Route updated successfully.");
          setEditing(false);
        }
        setEdited(false);
      } else {
        toast.error("The Route ID already exists.");
      }
    } catch (err) {
      toast.error("Error deleting Route.");
    }
  };

  const handleDelete = async e => {
    e.preventDefault();
    try {
      // Billed and Posted === 289
      const {
        data: { VW_Orders }
      } = await client.mutate({
        mutation: ALL_ORDERS_VIEW_QUERY,
        variables: {
          where: {
            RouteRecIdRef: parseInt(id),
            StatusRecIdRef_not: codes.OrdersBookingCode.RecId
          },
          first: 1
        },
        refetchQueries: ["ALL_ROUTES_QUERY", "SINGLE_ROUTE_QUERY"]
      });
      if (Array.isArray(VW_Orders) && VW_Orders.length > 0) {
        toast.error(
          "This route can not be deleted because has some orders actives"
        );
      } else {
        const performDelete = async () => {
          setLoading(true);
          setModalOpen(false, "");
          await client.mutate({
            mutation: DELETE_ROUTE_MUTATION,
            variables: {
              where: {
                RecId: parseInt(id)
              }
            },
            refetchQueries: ["ALL_ROUTES_QUERY", "SINGLE_ROUTE_QUERY"]
          });
          setLoading(false);
          toast.success("Route deleted successfully.");
          history.push("/dispatch/routes");
        };
        setModalOpen(
          true,
          <Confirmation
            message="Are you sure you want to delete?"
            onConfirm={performDelete}
          />
        );
      }
    } catch (err) {
      setLoading(false);
      toast.error("Error deleting Route.");
    }
  };

  const changeTab = tab => () => {
    if (edited) {
      handleSave(null, `/dispatch/routes/${inputs?.RecId}/${tab}`);
    } else {
      history.push(`/dispatch/routes/${inputs?.RecId}/${tab}`);
    }
  };

  if (
    loading ||
    data?.loading ||
    isLoading ||
    SystemCodesData?.loading ||
    !codes ||
    resUpdate?.loading
  )
    return <Loading />;
  return (
    <div className="route-page">
      {inputs?.RecId && (
        <React.Fragment>
          <header className="page-head">
            <div className="page-head-start">
              <Title color="primary" size={1}>
                {data?.Route?.RouteId || "Add Route"}
              </Title>
              <div className="page-head-start--subheader">
                <IconLink
                  icon="address-card"
                  label={data?.Route?.Customer?.Name}
                  to={`/finance/customers/${inputs?.CustomerRecIdRef}/details`}
                />
              </div>
            </div>
            <div className="page-head-end">
              <Field kind="group">
                {isAdmin && (
                  <React.Fragment>
                    <Control>
                      <Button
                        color="link"
                        size="small"
                        onClick={e =>
                          edited
                            ? handleSave(e, null, true)
                            : handleDirectionsPrint()
                        }
                      >
                        <Icon>
                          <FontAwesomeIcon icon="print" />
                        </Icon>
                        <span>Print Directions</span>
                      </Button>
                    </Control>
                    <Control>
                      <Button
                        color="danger"
                        size="small"
                        onClick={handleDelete}
                      >
                        <Icon>
                          <FontAwesomeIcon icon="trash" />
                        </Icon>
                        <span>Delete</span>
                      </Button>
                    </Control>
                  </React.Fragment>
                )}
                <Control>
                  <Button
                    color="primary"
                    disabled={editing}
                    size="small"
                    onClick={toggleEditing}
                  >
                    <Icon>
                      <FontAwesomeIcon icon="edit" />
                    </Icon>
                    <span>Edit</span>
                  </Button>
                </Control>
                <Control>
                  <Button
                    color="success"
                    disabled={!editing}
                    size="small"
                    onClick={handleSave}
                  >
                    <Icon>
                      <FontAwesomeIcon icon="save" />
                    </Icon>
                    <span>Save</span>
                  </Button>
                </Control>
              </Field>
            </div>
          </header>
          <Tab.Group kind="boxed">
            <Tab
              active={location.pathname.includes("details")}
              as={Button}
              style={{ cursor: "pointer" }}
              onClick={changeTab("details")}
            >
              <span>Details</span>
            </Tab>
            {isAuthorized && (
              <Tab
                active={location.pathname.includes("billing")}
                as={Button}
                style={{ cursor: "pointer" }}
                onClick={changeTab("billing")}
              >
                <span>Billing</span>
              </Tab>
            )}
          </Tab.Group>
        </React.Fragment>
      )}
      <Route path="/dispatch/routes/:id/billing">
        {BillingAuth(Billing, {
          route: inputs,
          onChange: handleChange,
          editing
        })}
      </Route>
      <Route path="/dispatch/routes/:id/details">
        <Details editing={editing} route={inputs} onChange={handleChange} />
      </Route>
      <Route path="/dispatch/routes/new">
        <AddRouteForm />
      </Route>
    </div>
  );
};

RoutePage.propTypes = {};
export default RoutePage;
